import React, { useState, useEffect, useContext, ChangeEvent } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { exportToExcelOrCsv } from "../../utils/common/ExportUtils.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { formatYNDisplay } from "../../services/itemImportStaging.ts";
import { ITEM_API_01 } from "../../api/ApiModel.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import {
  MSG_ITEMS_LOAD_FAILED,
  MSG_ITEM_DELETE_SUCCESS,
  MSG_ITEM_DELETE_FAILED,
  MSG_ITEM_DELETE_OPERATION_FAILED,
  MSG_ITEM_DELETE_NO_RESPONSE,
  MSG_ITEM_DELETE_UNKNOWN_ERROR,
  MSG_EXPORT_NO_DATA,
  MSG_EXPORT_SUCCESS,
  MSG_EXPORT_FAILED,
  MSG_EXPORT_SELECT_FIELD,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface User {
  USER_PARAM?: {
    COMPCODE?: string;
    STAFFMAPLOCODE?: string;
    USERNAME?: string;
  };
}

interface RootState {
  user: User;
}

interface VariantItem {
  variantcode?: number | string;
  variantname?: string;
  variantName?: string;
  variantvalue?: string;
  variantValue?: string;
}

interface ApiItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  endate?: string;
  uptype?: string;
  sts?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  packageunits?: number;
  descrip?: string;
  isselling?: string | null;
  isinv?: string | null;
  imagetype?: string;
  maincatname?: string;
  catname?: string;
  subcatname?: string;
  genericname?: string;
  brandname?: string;
  enuser?: string;
  [key: string]: any;
}

interface TransformedItem {
  id: string | number;
  itemcode: string;
  itemname: string;
  varianttype: string;
  createddate: string;
  endateRaw?: string;
  uptype: string;
  status: string;
  modelname: string;
  brandcode: string;
  maincatcode: string;
  catcode: string;
  subcatcode: string;
  genericcode: string;
  packagecode: string;
  measuringunitcode: string;
  packageunits: number;
  descrip: string;
  isselling?: string | null;
  isinv?: string | null;
  originalData: ApiItem;
}

interface ExportField {
  key: string;
  label: string;
  mandatory?: boolean;
}

interface ExportSelectedFields {
  [key: string]: boolean;
}

interface DeletePayload {
  productItemvariants: any[];
  dtoDOCs: any[];
  businesstypecode: string;
  itemcode: string | number;
  compcode?: string;
  loccode?: string;
  maincatcode: string | number;
  catcode: string | number;
  subcatcode: string | number;
  genericcode: string | number;
  packagecode: string | number;
  measuringunitcode: string | number;
  brandcode: string | number;
  itemname: string;
  modelname: string;
  packageunits: number;
  descrip: string;
  isselling?: string | null;
  isinv?: string | null;
  variantdata: string;
  imagetype: string;
  sts: string;
  enuser: string;
  endate: string;
}

interface ApiResponse {
  success?: boolean;
  status?: string;
  message?: string;
  errors?: Record<string, string>;
  title?: string;
  [key: string]: any;
}

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

/** Mandatory export fields — always selected and disabled in the dialog (left column). */
const EXPORT_MANDATORY_FIELDS: ExportField[] = [
  { key: "itemname", label: "Item Name", mandatory: true },
  { key: "brandname", label: "Brand Name", mandatory: true },
  { key: "maincatname", label: "Main Category", mandatory: true },
  { key: "catname", label: "Category", mandatory: true },
  { key: "subcatname", label: "Subcategory", mandatory: true },
  { key: "sts", label: "Status", mandatory: true },
];

/** Optional export fields — user can toggle (right column). */
const EXPORT_OPTIONAL_FIELDS: ExportField[] = [
  { key: "itemcode", label: "Item Code" },
  { key: "genericname", label: "Generic Name" },
  { key: "modelname", label: "Model Name" },
  { key: "packagename", label: "Package Name" },
  { key: "isselling", label: "Selling Item" },
  { key: "isinv", label: "Inventory Item" },
  { key: "packageunits", label: "Package Units" },
  { key: "measuringunit", label: "Measuring Unit" },
  { key: "descrip", label: "Description" },
  { key: "enuser", label: "User" },
  { key: "endate", label: "Date" },
];

const EXPORT_SELECTABLE_FIELDS: ExportField[] = [
  ...EXPORT_MANDATORY_FIELDS,
  ...EXPORT_OPTIONAL_FIELDS,
];

const buildDefaultExportFields = (optionalChecked = true): ExportSelectedFields =>
  EXPORT_SELECTABLE_FIELDS.reduce((acc: ExportSelectedFields, f: ExportField) => {
    acc[f.key] = f.mandatory ? true : optionalChecked;
    return acc;
  }, {});

function ViewCreateItem(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageData } = usePageData();

  // Get user from Redux store
  const SYS_USER: User = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<TransformedItem[]>([]);
  const [filteredRows, setFilteredRows] = useState<TransformedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedItem, setSelectedItem] = useState<TransformedItem | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Export dropdown and field-selection dialog
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportSelectedDialogOpen, setExportSelectedDialogOpen] = useState<boolean>(false);
  const [selectedExportFields, setSelectedExportFields] = useState<ExportSelectedFields>(() =>
    buildDefaultExportFields(true)
  );

  useEffect(() => {
    setPageData({ title: "Item Management" });
    fetchItems();
  }, [COMPCODE, STAFFMAPLOCODE, refreshTrigger, setPageData]);

  // Helper function to format variant data for display
  const formatVariantData = (variantData?: string | null): string => {
    if (!variantData || variantData === "" || variantData === "None") {
      return "None";
    }

    try {
      let parsed: VariantItem[] = [];
      const str = typeof variantData === "string" ? variantData.trim() : String(variantData);

      // API format: JSON string array e.g. "[{\"variantcode\":1,\"variantname\":\"Size\",\"variantvalue\":\"3\"},...]"
      if (str.startsWith("[")) {
        try {
          let cleaned = str.replace(/\\"/g, '"');
          if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
            cleaned = cleaned.slice(1, -1);
          }
          parsed = JSON.parse(cleaned);
        } catch (e) {
          parsed = JSON.parse(str);
        }
      } else {
        // Legacy format: "Type1: val1, val2; Type2: val3"
        const entries = str.split(";").map((v: string) => v.trim()).filter(Boolean);
        parsed = entries.map((entry: string) => {
          const [type, valuesStr] = entry.split(":").map((s: string) => s.trim());
          if (!type || !valuesStr) return null;
          const values = valuesStr.split(",").map((v: string) => v.trim()).filter(Boolean);
          return values.map((val: string) => ({ variantname: type, variantvalue: val }));
        }).flat().filter(Boolean) as VariantItem[];
      }

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return "None";
      }

      // Group by variantname and format as "Name: value1, value2"
      const grouped: Record<string, string[]> = {};
      parsed.forEach((v: VariantItem) => {
        const name = (v.variantname || v.variantName || "").trim();
        const val = (v.variantvalue || v.variantValue || "").trim();
        if (!name) return;
        
        // Only add to the group if there is an actual value
        if (val) {
          if (!grouped[name]) grouped[name] = [];
          if (!grouped[name].includes(val)) grouped[name].push(val);
        }
      });

      // FIX: Filter out variant names that ended up with no values
      const formatted = Object.entries(grouped)
        .filter(([_, values]) => values.length > 0) // <--- This line is the fix
        .map(([name, values]) => `${name}: ${values.join(", ")}`)
        .join(" | ");

      // If no variants had values, it's a normal item
      return formatted || "None"; 
    } catch (error) {
      console.error("Error formatting variant data:", error);
      return variantData || "None";
    }
  };

  // Fetch items from API
  const fetchItems = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Prepare parameters
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      };

      // Call the API to get items
      const response = await externalgetApi(ITEM_API_01, "GET", params);
      
      // Normalize: API may return object { "85": {...}, "86": {...} } or array
      let itemsList: ApiItem[] = [];
      if (response && Array.isArray(response)) {
        itemsList = response;
      } else if (response && typeof response === "object" && !Array.isArray(response)) {
        itemsList = Object.values(response);
      }

      if (itemsList.length > 0) {
        // Transform API response to match grid structure
        const transformedData: TransformedItem[] = itemsList.map((item: ApiItem, index: number) => ({
          id: item.itemcode || index + 1,
          itemcode: item.itemcode?.toString() || "",
          itemname: item.itemname || "",
          varianttype: formatVariantData(item.variantdata),
          createddate: formatDate(item.endate),
          uptype: item.uptype || "",
          endateRaw: item.endate || "",
          status: getStatusText(item.sts),
          modelname: item.modelname || "",
          brandcode: item.brandcode?.toString() || "",
          maincatcode: item.maincatcode?.toString() || "",
          catcode: item.catcode?.toString() || "",
          subcatcode: item.subcatcode?.toString() || "",
          genericcode: item.genericcode?.toString() || "",
          packagecode: item.packagecode?.toString() || "",
          measuringunitcode: item.measuringunitcode?.toString() || "",
          packageunits: item.packageunits || 0,
          descrip: item.descrip || "",
          isselling: item.isselling ?? item.ISSELLING ?? "",
          isinv: item.isinv ?? item.ISINV ?? "",
          // Store original data for editing
          originalData: item
        }));

        const sortByImport = (location.state as { sortByImport?: boolean })?.sortByImport;
        const compareItemCodeDesc = (a: TransformedItem, b: TransformedItem): number => {
          const aNum = Number(a.itemcode);
          const bNum = Number(b.itemcode);
          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return bNum - aNum;
          return String(b.itemcode || "").localeCompare(String(a.itemcode || ""), undefined, {
            numeric: true,
          });
        };
        const sortedData = sortByImport
          ? [...transformedData].sort((a, b) => {
              const importRank = (row: TransformedItem) => (row.uptype === "2" || row.uptype === 2 ? 0 : 1);
              const rankDiff = importRank(a) - importRank(b);
              if (rankDiff !== 0) return rankDiff;
              return compareItemCodeDesc(a, b);
            })
          : [...transformedData].sort(compareItemCodeDesc);

        setData(sortedData);
        setFilteredRows(sortedData);
      } else {
        setData([]);
        setFilteredRows([]);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      setAlertMessage(MSG_ITEMS_LOAD_FAILED);
      setAlertType("error");
      setAlertOpen(true);
      setData([]);
      setFilteredRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to get status text
  const getStatusText = (statusCode?: string): string => {
    switch(statusCode) {
      case CON_STATUS_ACTIVE:
        return "Active";
      case "I":
        return "Inactive";
      case "D":
        return "Deleted";
      default:
        return "Active";
    }
  };

const handleSearchChange = (event: ChangeEvent<{}>, value: string | null): void => {
  const searchValue = value || "";
  setSearchQuery(searchValue);
  
  if (!searchValue.trim()) {
    setFilteredRows(data);
    return;
  }

  const q = searchValue.toLowerCase().trim();
  const safeStr = (val?: any): string => (val != null ? String(val).toLowerCase() : "");
  
  // First, filter the data based on search criteria
  const filtered = data.filter((row: TransformedItem) => {
    const o = row.originalData || {};
    return (
      safeStr(row.itemcode).includes(q) ||
      safeStr(row.itemname).includes(q) ||
      safeStr(o.maincatname).includes(q) ||
      safeStr(o.catname).includes(q) ||
      safeStr(o.subcatname).includes(q) ||
      safeStr(o.genericname).includes(q) ||
      safeStr(o.brandname).includes(q) ||
      safeStr(row.status).includes(q) ||
      safeStr(row.createddate).includes(q) ||
      safeStr(row.varianttype).includes(q) ||           
      safeStr(row.uptype).includes(q) ||                 
      safeStr(row.createddate).includes(q)               
    );
  });
  
  // Now sort the filtered results to prioritize items that START with the search term
  const sortedFiltered = [...filtered].sort((a, b) => {
    const aItemName = safeStr(a.itemname);
    const bItemName = safeStr(b.itemname);
    
    // Check if item names start with the search term
    const aStartsWith = aItemName.startsWith(q);
    const bStartsWith = bItemName.startsWith(q);
    
    // If one starts with the term and the other doesn't, prioritize the one that starts with it
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    
    // If both start with the term or both don't, sort alphabetically
    if (aStartsWith && bStartsWith) {
      return aItemName.localeCompare(bItemName);
    }
    
    // For items that don't start with the term, sort by relevance (position of search term)
    const aIndex = aItemName.indexOf(q);
    const bIndex = bItemName.indexOf(q);
    
    if (aIndex !== -1 && bIndex !== -1) {
      // Sort by position of the search term (earlier position = higher priority)
      if (aIndex !== bIndex) return aIndex - bIndex;
      // If same position, sort alphabetically
      return aItemName.localeCompare(bItemName);
    }
    
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    // Default alphabetical sort for items that don't contain the term (shouldn't happen since we filtered)
    return aItemName.localeCompare(bItemName);
  });
  
  setFilteredRows(sortedFiltered);
};

  const handleItemClick = (id: string | number, actionText: string): void => {
    const item = data.find((row: TransformedItem) => row.itemcode === id);
    if (!item) return;

    if (actionText === "View") {
      // Navigate to ViewItemData page with item data
      navigate("/viewitems", {
        state: {
          selectedItem: item.originalData,
          actionText: "View",
          itemData: item
        },
      });
    }

    if (actionText === "Edit") {
      // Pass the complete item data to the edit page
      navigate("/vCreateItem", {
        state: {
          selectedItem: item.originalData,
          actionText: "Edit",
          // Pass additional transformed data if needed
          itemData: item
        },
      });
    }

    if (actionText === "Delete") {
      setSelectedId(id);
      setSelectedItem(item);
      setOpenDeleteDialog(true);
    }
  };

  // Add button
  const addToView = (): void =>
    navigate("/vCreateItem", { state: { actionText: "Add" } });

const confirmDelete = async (): Promise<void> => {
  if (!selectedItem) return;

  try {
    setIsLoading(true);

    // Get original data
    const originalData = selectedItem.originalData || {};

    // Build COMPLETE payload matching the API specification
    const payload: DeletePayload = {
      // Required by API spec
      productItemvariants: [], // Empty array since we're deleting
      dtoDOCs: [], // Empty array for documents
      businesstypecode: "O", // Required field
      
      // Item identification
      itemcode: selectedItem.itemcode || 0,
      compcode: COMPCODE,
      loccode: STAFFMAPLOCODE,
      
      // Category information (required for update)
      maincatcode: originalData.maincatcode || 0,
      catcode: originalData.catcode || 0,
      subcatcode: originalData.subcatcode || 0,
      genericcode: originalData.genericcode || 0,
      packagecode: originalData.packagecode || 0,
      measuringunitcode: originalData.measuringunitcode || 0,
      brandcode: originalData.brandcode || 0,
      
      // Item details (required)
      itemname: originalData.itemname || "",
      modelname: originalData.modelname || "",
      packageunits: originalData.packageunits || 0,
      descrip: originalData.descrip || "",
      isselling: originalData.isselling ?? originalData.ISSELLING ?? null,
      isinv: originalData.isinv ?? originalData.ISINV ?? null,
      variantdata: originalData.variantdata || "",
      imagetype: originalData.imagetype || "common",
      
      // Status for delete
      sts: "D", // Mark as deleted
      enuser: USERNAME || "Admin",
      endate: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
    };

    console.log("Deleting item payload:", JSON.stringify(payload, null, 2));

    // Send the DELETE/UPDATE request
    const response: ApiResponse = await externalpostApi(ITEM_API_01, payload);

    console.log("DELETE response:", response);

    // Handle response
    if (response) {
      // Check if response indicates success
      // Different APIs might return different success indicators
      if (response.success === false || response.status === 'error') {
        throw new Error(response.message || MSG_ITEM_DELETE_OPERATION_FAILED);
      }
      
      // Remove deleted item from local state
      setData((prev: TransformedItem[]) => prev.filter((row: TransformedItem) => row.itemcode !== selectedItem.itemcode));
      setFilteredRows((prev: TransformedItem[]) => prev.filter((row: TransformedItem) => row.itemcode !== selectedItem.itemcode));

      setAlertMessage(MSG_ITEM_DELETE_SUCCESS(selectedItem.itemname));
      setAlertType("success");
      setAlertOpen(true);
      
      // Refresh data from server
      setRefreshTrigger((prev: number) => prev + 1);
    } else {
      throw new Error("No response received from server");
    }
    
  } catch (error: any) {
    console.error("DELETE API error details:", error);
    
    let errorMessage = MSG_ITEM_DELETE_FAILED;
    
    // Extract error message
    if (error.response) {
      const errorData = error.response.data;
      console.error("Server error response:", errorData);
      
      if (typeof errorData === 'string') {
        errorMessage += errorData;
      } else if (errorData && typeof errorData === 'object') {
        // Handle validation errors or structured errors
        if (errorData.errors) {
          // If there are field-specific errors
          const fieldErrors = Object.values(errorData.errors).join(', ');
          errorMessage += fieldErrors;
        } else if (errorData.title || errorData.message) {
          errorMessage += (errorData.title || errorData.message);
        } else {
          errorMessage += JSON.stringify(errorData);
        }
      } else {
        errorMessage += `Server returned ${error.response.status}: ${error.response.statusText}`;
      }
    } else if (error.request) {
      errorMessage += MSG_ITEM_DELETE_NO_RESPONSE;
    } else {
      errorMessage += error.message || MSG_ITEM_DELETE_UNKNOWN_ERROR;
    }

    setAlertMessage(errorMessage);
    setAlertType("error");
    setAlertOpen(true);
    
  } finally {
    setIsLoading(false);
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  }
};

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  // Build export rows from originalData (API item) for the given field keys
  const buildExportData = (fieldKeys: string[]): Record<string, string>[] => {
    return data.map((row: TransformedItem) => {
      const o = row.originalData || {};
      const record: Record<string, string> = {};
      fieldKeys.forEach((key: string) => {
        if (key === "endate") {
          record[key] = formatDate(o.endate) || o.endate || "";
        } else if (key === "isselling") {
          record[key] = formatYNDisplay(o.isselling ?? o.ISSELLING);
        } else if (key === "isinv") {
          record[key] = formatYNDisplay(o.isinv ?? o.ISINV);
        } else {
          record[key] = o[key] != null ? String(o[key]) : "";
        }
      });
      return record;
    });
  };

  const getColumnNamesForFields = (fieldKeys: string[]): Record<string, string> => {
    return fieldKeys.reduce(
      (acc: Record<string, string>, key: string) => {
        const def = EXPORT_SELECTABLE_FIELDS.find((f: ExportField) => f.key === key);
        acc[key] = def ? def.label : key;
        return acc;
      },
      {}
    );
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = (): void => {
    setExportMenuAnchor(null);
  };

  // Export All (XLS): export all item list with all 15 fields
  const handleExportAll = (): void => {
    handleExportMenuClose();
    try {
      if (!data.length) {
        setAlertMessage(MSG_EXPORT_NO_DATA);
        setAlertType("warning");
        setAlertOpen(true);
        return;
      }
      const fieldKeys = EXPORT_SELECTABLE_FIELDS.map((f: ExportField) => f.key);
      const exportData = buildExportData(fieldKeys);
      const columnNames = getColumnNamesForFields(fieldKeys);
      const fileName = `items_export_all_${new Date().toISOString().split("T")[0]}`;
      exportToExcelOrCsv(exportData, columnNames, {
        fileName,
        sheetName: "Item List",
        format: "xlsx",
      });
      setAlertMessage(MSG_EXPORT_SUCCESS);
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Export error:", error);
      setAlertMessage(MSG_EXPORT_FAILED);
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  // Open Export Selected Field dialog
  const handleExportSelectedClick = (): void => {
    handleExportMenuClose();
    setExportSelectedDialogOpen(true);
  };

  const handleExportSelectedDialogClose = (): void => {
    setExportSelectedDialogOpen(false);
  };

  const handleExportFieldToggle = (key: string): void => {
    if (EXPORT_MANDATORY_FIELDS.some((f) => f.key === key)) return;
    setSelectedExportFields((prev: ExportSelectedFields) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExportSelectAllFields = (): void => {
    setSelectedExportFields(buildDefaultExportFields(true));
  };

  const handleExportDeselectAllFields = (): void => {
    setSelectedExportFields(buildDefaultExportFields(false));
  };

  // Export Selected Field (XLS): export mandatory fields + user-selected optional fields
  const handleExportSelectedConfirm = (): void => {
    const fieldKeys = EXPORT_SELECTABLE_FIELDS.filter(
      (f: ExportField) => f.mandatory || selectedExportFields[f.key]
    ).map((f: ExportField) => f.key);
    if (!fieldKeys.length) {
      setAlertMessage(MSG_EXPORT_SELECT_FIELD);
      setAlertType("warning");
      setAlertOpen(true);
      return;
    }
    try {
      if (!data.length) {
        setAlertMessage(MSG_EXPORT_NO_DATA);
        setAlertType("warning");
        setAlertOpen(true);
        return;
      }
      const exportData = buildExportData(fieldKeys);
      const columnNames = getColumnNamesForFields(fieldKeys);
      const fileName = `items_export_selected_${new Date().toISOString().split("T")[0]}`;
      exportToExcelOrCsv(exportData, columnNames, {
        fileName,
        sheetName: "Item List",
        format: "xlsx",
      });
      setExportSelectedDialogOpen(false);
      setAlertMessage(MSG_EXPORT_SUCCESS);
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Export error:", error);
      setAlertMessage(MSG_EXPORT_FAILED);
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const handleImport = (): void => {
    navigate("/data-migration");
  };

  // Add View option to menuItems
  const menuItems: MenuItemType[] = [
    {
      text: "View",
      icon: <VisibilityIcon />,
      onClick: (id: string | number) => handleItemClick(id, "View"),
    },
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleItemClick(id, "Edit"),
    },
    {
      text: "Delete",
      icon: <DeleteIcon />,
      onClick: (id: string | number) => handleItemClick(id, "Delete"),
    },
  ];

  // Define grid columns
  const itemGridData = {
    name: "Item List",
    columns: [
      {
        field: "itemcode",
        headerName: "Item Code",
        width: 120, // Item code usually stays fixed
        headerAlign: 'center' as const,
        align: 'center' as const,
      },
      {
        field: "itemname",
        headerName: "Item Name",
        // Use flex to allow smooth resizing and filling available space
        flex: 1, 
        minWidth: 200, 
        headerAlign: 'left' as const,
        align: 'left' as const,
      },
      {
        field: "varianttype",
        headerName: "Variant Type",
        // Use flex to allow smooth resizing
        flex: 1, 
        minWidth: 200,
        headerAlign: 'left' as const,
        align: 'left' as const,
        renderCell: (params: any) => {
          const fullText = params.value || "None";
          return (
            <Tooltip title={fullText} arrow placement="top">
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>
                {fullText}
              </span>
            </Tooltip>
          );
        }
      },
            {
        field: "createddate",
        headerName: "Created Date",
        width: 150,
        minWidth: 120,
        maxWidth: 300,
        headerAlign: "center", // This now works perfectly
        align: "center",       // This now works perfectly
        renderCell: (params: any) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {params.value || ""}
          </Box>
        ),
      },
    ],
  };

  // Prepare rows WITHOUT actions column - each row already has an 'id' property
  const preparedRows = filteredRows.map((row: TransformedItem) => ({
    ...row,
    id: row.itemcode || Math.random(),
  }));

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Breadcrumbs separator="›" sx={styles.breadcrumb}>
          <Link underline="hover" sx={styles.breadcrumb_link}>Home</Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>Item Management</Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>Create Items</Link>
          <Typography sx={styles.breadcrumb_link_page}>
            Item List
          </Typography>
        </Breadcrumbs>
      </Box>
      <Box
        sx={{ ...styles.box_page_container, width: "100%" }}
      >

        <AppBar sx={styles.appbar}>
          <Grid
            container
            spacing={2}
            sx={{
              ...styles.appbar_grid_container,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Left: Title */}
            <Grid>
              <Typography sx={styles.appbar_title}>
                {itemGridData.name}
              </Typography>
            </Grid>

            {/* Right: Search + Buttons */}
            <Grid sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Search Autocomplete */}
              <Autocomplete
                freeSolo
                id="search-item"
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                options={data?.map((row: TransformedItem) => row.itemname) || []}
                sx={{ ...styles.ipt_appbar_search_auto, minWidth: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by..."
                    size="small"
                    sx={styles.ipt_appbar_search}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      type: "search",
                      endAdornment: (
                        <InputAdornment position="end">
                                          <IconButton
                                            id="btn_appbarsearch"
                                            sx={styles.btn_appbar_search_icon}
                                          >
                                            <SearchIcon id="ico_search" />
                                          </IconButton>
                                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Export Button with dropdown: Export All (XLS) / Export Selected Field (XLS) */}
<Button
  variant="outlined"
  startIcon={<DownloadIcon />}
  endIcon={<ArrowDropDownIcon />}
  size="small"
  sx={{ 
    height: 40,
    textTransform: 'none' 
  }}
  onClick={handleExportMenuOpen}
  disabled={data.length === 0}
>
  Export
</Button>
<Menu
  anchorEl={exportMenuAnchor}
  open={Boolean(exportMenuAnchor)}
  onClose={handleExportMenuClose}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  transformOrigin={{ vertical: "top", horizontal: "right" }}
>
  <MenuItem onClick={handleExportAll}>Full Report</MenuItem>
  <MenuItem onClick={handleExportSelectedClick}>
    Customize Export
  </MenuItem>
</Menu>

{/* Import Button */}
<Button
  variant="outlined"
  startIcon={<UploadIcon />}
  size="small"
  sx={{ height: 40, textTransform: 'none' }}
  onClick={handleImport}
>
  Import
</Button>
<Button
  variant="outlined"
  size="small"
  sx={{ height: 40, textTransform: 'none' }}
  onClick={() => navigate("/unverified-items")}
>
  Unverified Items
</Button>

{/* Add Button */}
<Button
  variant="contained"
  startIcon={<AddCircleOutlineIcon />}
  size="small"
  sx={{ 
    height: 40,
    textTransform: 'none' // This prevents uppercase
  }}
  onClick={addToView}
>
  Add
</Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={{
          ...styles.view_page_container,
          width: '100%',
          '& .MuiDataGrid-root': {
            width: '100% !important',
          },
          '& .MuiDataGrid-main': {
            width: '100% !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            width: '100% !important',
          },
          '& .MuiDataGrid-columnHeaders': {
            width: '100% !important',
          },
          '& .MuiDataGrid-footerContainer': {
            width: '100% !important',
          }
        }}>
<ViewDataGrid
  columns={itemGridData.columns}
  rows={preparedRows}
  menuItems={menuItems}
  tabletitle={itemGridData.name}
  gridid="itemcode"
  processRowUpdate={(newRow: any) => {
    // Handle row update if needed
    return newRow;
  }}
/>
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <AppDialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        titleId="delete-dialog-title"
        title="Confirm Delete"
        message={`Are you sure you want to delete item "${selectedItem?.itemname}" (Code: ${selectedItem?.itemcode})?`}
        cancelLabel="Cancel"
        confirmLabel={isLoading ? "Deleting..." : "Delete"}
        confirmColor="error"
        onConfirm={confirmDelete}
        confirmLoading={isLoading}
        cancelDisabled={isLoading}
      />

      {/* Export Selected Field dialog: mandatory (left) + optional (right) */}
      <AppDialog
        open={exportSelectedDialogOpen}
        onClose={handleExportSelectedDialogClose}
        titleId="export-selected-dialog-title"
        title="Select Fields to Export"
        message="Choose the fields to include in the exported Excel file."
        maxWidth="sm"
        fullWidth
        cancelLabel="Cancel"
        confirmLabel="Export"
        confirmColor="primary"
        onConfirm={handleExportSelectedConfirm}
      >
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Button size="small" variant="outlined" onClick={handleExportSelectAllFields}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleExportDeselectAllFields}>
            Deselect All
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            alignItems: "start",
          }}
        >
          <Box>
            <FormGroup sx={{ gap: 0 }}>
              {EXPORT_MANDATORY_FIELDS.map((field: ExportField) => (
                <FormControlLabel
                  key={field.key}
                  sx={{ m: 0, py: 0.15 }}
                  control={<Checkbox size="small" checked disabled />}
                  label={field.label}
                />
              ))}
            </FormGroup>
          </Box>
          <Box>
            <FormGroup sx={{ gap: 0 }}>
              {EXPORT_OPTIONAL_FIELDS.map((field: ExportField) => (
                <FormControlLabel
                  key={field.key}
                  sx={{ m: 0, py: 0.15 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!selectedExportFields[field.key]}
                      onChange={() => handleExportFieldToggle(field.key)}
                    />
                  }
                  label={field.label}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </AppDialog>
    </>
  );
}

export default ViewCreateItem;